<script lang="ts" setup>
/* eslint no-useless-escape: "off" */
import { faSpinnerThird } from "@fortawesome/pro-regular-svg-icons";
import WebFrame from "~/components/mail/WebFrame.vue";
import { render } from "vue";

export interface HdIFrameProps {
    asWebFrame?: boolean;
    waitingForData?: boolean;
    src: string;
    crossorigin?: string;
    target?: string;
    frameClass?: string;
    allow?: string;
    scrolling?: string;
    name?: string;
    title?: string;
    keep?: string[];
    sandbox?: string;
    scale?: number;
    retries?: number;
    position: "relative" | "absolute" | "fixed" | "sticky";
    retryDelay?: number;
}

const props = withDefaults(defineProps<HdIFrameProps>(), {
    scale: 1,
    crossorigin: "anonymous",
    target: "_parent",
    keep: () => [],
    position: "relative",
    retries: 1,
    retryDelay: 2000,
});

const slots = useSlots();

const emit = defineEmits<{
    (e: "frame-loaded", v: MessageEvent): void;
    (e: "loaded", v: MessageEvent): void;
}>();

const body = ref<HTMLDivElement>();
const loader = ref<HTMLDivElement>();
const frame = ref<HTMLIFrameElement>();
const onLoadedMessage = ref<string>(`IFRAME_LOADED_${useId()}`);
const onReadyStateChangeMessage = ref<string>(
    `IFRAME_ON_READ_STATE_CHANGE_${useId()}`,
);
const currentStyle = ref<string>("");
const frameLoaded = ref<boolean>(false);
const loaded = ref<boolean>(false);
const attempts = ref<number>(0);

function removeFrame() {
    Array.from(body?.value?.children ?? []).forEach((child) => {
        if (child !== loader.value && !props.keep.includes(child.id)) {
            body.value?.removeChild(child);
        }
    });

    loaded.value = false;
}

function setFrameUrl() {
    if (frame.value?.contentWindow === null) {
        setTimeout(setFrameUrl);
        return;
    }

    frame.value.src = props.src;
}

function initializeFrame() {
    if (props.waitingForData) {
        setTimeout(() => initializeFrame(), 10);
        return;
    }

    currentStyle.value =
        "visibility: hidden; position: absolute; top: -99999px; border: none;";
    if (props.asWebFrame) {
        const div = document.createElement("div");

        const finalize = () => {
            if (frame.value) {
                setStyle();
                if (props.frameClass)
                    frame.value.setAttribute("class", props.frameClass);
                if (props.crossorigin)
                    frame.value.setAttribute("crossorigin", props.crossorigin);
                if (props.target)
                    frame.value.setAttribute("target", props.target);
                if (props.allow) frame.value.setAttribute("allow", props.allow);
                if (props.scrolling)
                    frame.value.setAttribute("scrolling", props.scrolling);
                if (props.name) frame.value.setAttribute("name", props.name);
                if (props.title) frame.value.setAttribute("title", props.title);
                if (props.sandbox)
                    frame.value.setAttribute("sandbox", props.sandbox);
                body.value?.appendChild(frame.value);
                currentStyle.value = "visibility: visible; border: none;";
                setStyle();
                frameLoaded.value = loaded.value = true;
            }
        };

        if (frame.value) {
            (frame.value as HTMLIFrameElement).srcdoc = props.src;

            finalize();
        } else {
            render(
                h(WebFrame, {
                    document: props.src,
                    onVnodeMounted: (vnode) => {
                        frame.value = div.firstChild as HTMLIFrameElement;
                        finalize();
                    },
                    children: slots.$default ? [slots.$default] : [],
                }),
                div,
                false,
            );
        }
    } else {
        // eslint-disable-next-line
        frame.value = document.createElement("iframe");
        if (props.src) frame.value.setAttribute("iframe-src", props.src);
        setStyle();
        if (props.frameClass)
            frame.value.setAttribute("class", props.frameClass);
        if (props.crossorigin)
            frame.value.setAttribute("crossorigin", props.crossorigin);
        if (props.sandbox)
            frame.value.setAttribute("sandbox", props.sandbox);
        if (props.target) frame.value.setAttribute("target", props.target);
        if (props.allow) frame.value.setAttribute("allow", props.allow);
        if (props.scrolling)
            frame.value.setAttribute("scrolling", props.scrolling);
        if (props.name) frame.value.setAttribute("name", props.name);
        if (props.title) frame.value.setAttribute("title", props.title);
        if (props.sandbox) frame.value.setAttribute("sandbox", props.sandbox);
        body.value?.appendChild(frame.value);
        frame.value.onload = () => {
            eventListener({ data: onLoadedMessage.value } as MessageEvent);
            eventListener({ data: onReadyStateChangeMessage.value } as MessageEvent);
        };

        setFrameUrl();
    }
}

function eventListener(event: MessageEvent) {
    if (event.data === onLoadedMessage.value) {
        frameLoaded.value = true;
        emit("frame-loaded", event);

        currentStyle.value = "visibility: visible; border: none;";
        setStyle();
    }

    if (event.data === onReadyStateChangeMessage.value) {
        loaded.value = true;
        emit("loaded", event);
    }
}

const reinitialize = useDebounce(() => {
    removeFrame();
    initializeFrame();

    if (!frameLoaded.value && attempts.value < props.scale) {
        attempts.value += 1;
        setTimeout(() => {
            if (!frameLoaded.value) reinitialize();
        }, props.retryDelay);
    }
});

const size = useElementSize(body);

const scale = computed(() => {
    if (props.scale !== 1) {
        const scale = 1 / props.scale;

        return `
            width: ${size.width.value * props.scale}px;
            height: ${size.height.value * props.scale}px;
            -moz-transform: scale(${scale}, ${scale});
            -webkit-transform: scale(${scale}, ${scale});
            -o-transform: scale(${scale}, ${scale});
            -ms-transform: scale(${scale}, ${scale});
            transform: scale(${scale}, ${scale});
            -moz-transform-origin: top left;
            -webkit-transform-origin: top left;
            -o-transform-origin: top left;
            -ms-transform-origin: top left;
            transform-origin: top left;
        `;
    }

    return `
            width: ${size.width.value}px;
            height: ${size.height.value}px;
    `;
});

const setStyle = useDebounce(
    () => {
        frame.value?.setAttribute(
            "style",
            `${currentStyle.value}; ${scale.value}`.trim(),
        );
    },
    10,
    {
        leading: true,
        trailing: true,
    },
);

watchImmediate([size.width, size.height], () => setStyle());

watchImmediate(
    () => props.src,
    () => reinitialize(),
);

watch(
    () => props.frameClass,
    (to) => {
        if (frame.value) {
            frame.value?.setAttribute("class", to ? to : "");
        }
    },
);

onMounted(() => {
    window.addEventListener("message", eventListener, false);
});

onBeforeUnmount(() => {
    window.removeEventListener("message", eventListener, false);
});
</script>

<template>
    <div ref="body" :class="position">
        <div v-if="!loaded || waitingForData" ref="loader"
            class="absolute w-full flex-1 flex items-center h-full justify-center z-10">
            <FontAwesomeIcon :icon="faSpinnerThird" class="text-primary-500" size="2x" spin />
        </div>
        <slot name="overlay" />
    </div>
</template>
